import { OtsSpanTimeline } from "../components/OtsSpanTimeline";
import { formatActivityDuration, formatActivityTime } from "./driver-activity-format";
import { activityKindTheme, activitySpanTone } from "./driver-activity-theme";
import type { ActivityDriver, ActivityEntry } from "./driver-activity-view-model";

export type ActivityDayTimelineProps = {
  driver: ActivityDriver;
  timeZone?: string;
  /** "Now", the end of the axis while a shift is open. */
  now?: number;
  activeKey?: string;
  onSelect?: (entry: ActivityEntry) => void;
  className?: string;
};

const title = (entry: ActivityEntry) => {
  const name = entry.pickup?.ticketNumber ?? entry.delivery?.ticketNumber
    ?? entry.geofenceEvent?.siteName ?? entry.serviceWork?.siteName;
  return [activityKindTheme[entry.kind].label, name].filter(Boolean).join(" · ");
};

/** A driver's day at a glance: every load, site visit, and job laid out across their shift hours. */
export const ActivityDayTimeline = ({ driver, timeZone, now = Date.now(), activeKey, onSelect, className }: ActivityDayTimelineProps) => {
  const entries = driver.shifts.flatMap((shift) => shift.entries)
    .filter((entry) => entry.time != null && entry.kind !== "dispatch" && entry.kind !== "tankDrop");
  const shifts = driver.shifts.flatMap(({ shift }) => shift ? [shift] : []);
  const starts = [...shifts.map((shift) => shift.startDttm), ...entries.map((entry) => entry.time as number)];
  if (!starts.length) return null;

  const start = Math.min(...starts);
  const end = Math.max(
    ...shifts.map((shift) => shift.endDttm ?? now),
    ...entries.map((entry) => entry.endTime ?? (entry.time as number)),
    start + 60_000
  );

  return (
    <OtsSpanTimeline
      className={className}
      ariaLabel={`${driver.name}'s day`}
      start={start}
      end={end}
      startLabel={formatActivityTime(start, timeZone)}
      endLabel={formatActivityTime(end, timeZone)}
      activeId={activeKey}
      onSelect={onSelect && ((id) => {
        const entry = entries.find((candidate) => candidate.key === id);
        if (entry) onSelect(entry);
      })}
      spans={entries.map((entry) => ({
        id: entry.key,
        start: entry.time as number,
        // A one-sided ticket has no end; give it a visible sliver rather than running to the end of the day.
        end: entry.endTime ?? (entry.kind === "pickup" || entry.kind === "delivery" ? (entry.time as number) + 10 * 60_000 : undefined),
        tone: activitySpanTone(entry),
        label: title(entry),
        detail: [
          `${formatActivityTime(entry.time, timeZone)}${entry.endTime != null ? ` – ${formatActivityTime(entry.endTime, timeZone)}` : ""}`,
          entry.durationMillis != null ? formatActivityDuration(entry.durationMillis) : undefined,
          entry.warnings[0]?.message,
        ].filter(Boolean).join(" · "),
      }))}/>
  );
};
